import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { socket } from '../socket';
import { clearActiveRoomCode } from '../utils/activeRoomCode';

export default function HostPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [status, setStatus] = useState('waiting'); // 'waiting' | 'active' | 'paused' | 'ended'
  const [roster, setRoster] = useState([]); // [{ socketId, name }] in join order
  const [calledWords, setCalledWords] = useState([]);   // history, oldest first
  const [currentWord, setCurrentWord] = useState(null); // { word, definition }
  const [remaining, setRemaining] = useState(state?.totalWords ?? 0);
  const [totalWords, setTotalWords] = useState(state?.totalWords ?? 0);
  const [displayMode, setDisplayMode] = useState(state?.displayMode || 'word');
  const [winners, setWinners] = useState([]); // ordered list of bingo claimants
  const [connected, setConnected] = useState(socket.connected);
  const [ending, setEnding] = useState(false);
  const [calling, setCalling] = useState(false);
  const [rejoining, setRejoining] = useState(false);
  const [rejoinError, setRejoinError] = useState(null);

  // True when we need to re-register this socket with the server room.
  // Set on mount if the socket isn't connected yet (page refresh / direct nav),
  // and set again whenever the socket drops so the next reconnect triggers a rejoin.
  const needsRejoinRef = useRef(!socket.connected);

  // Mirrors totalWords for the socket-listener effect below, so that effect
  // doesn't need totalWords in its own dependency array (which would tear
  // down and rebuild all 11 listeners on every reconnect).
  const totalWordsRef = useRef(totalWords);
  useEffect(() => {
    totalWordsRef.current = totalWords;
  }, [totalWords]);

  const attemptRejoin = useCallback(() => {
    setRejoining(true);
    socket.emit('game:teacher_rejoin', { code }, (res) => {
      if (res?.error) {
        clearActiveRoomCode();
        setRejoinError(res.error);
        setRejoining(false);
        socket.disconnect();
        return;
      }
      // Restore full room state from server
      setStatus(res.status);
      setRoster(res.roster || []);
      setCalledWords(res.calledWords || []);
      if (res.calledWords?.length > 0) {
        setCurrentWord(res.calledWords[res.calledWords.length - 1]);
      }
      setTotalWords(res.totalWords);
      setRemaining(res.remaining);
      setDisplayMode(res.displayMode || 'word');
      setWinners(res.winners || []);
      setConnected(true);
      setRejoining(false);
      needsRejoinRef.current = false;
    });
  }, [code]);

  const handleDisconnect = useCallback(() => {
    setConnected(false);
    needsRejoinRef.current = true; // next connect should rejoin
  }, []);

  const handleConnect = useCallback(() => {
    setConnected(true);
    if (needsRejoinRef.current) {
      attemptRejoin();
    }
  }, [attemptRejoin]);

  useEffect(() => {
    if (!socket.connected) {
      // Socket not connected: connect now; handleConnect will attempt the rejoin
      socket.connect();
    }

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    socket.on('game:player_joined', ({ roster: r }) => setRoster(r || []));
    socket.on('game:player_left', ({ roster: r }) => setRoster(r || []));
    socket.on('game:player_disconnected', ({ roster: r }) => setRoster(r || []));
    socket.on('game:player_reconnected', ({ roster: r }) => setRoster(r || []));
    socket.on('game:player_marked', ({ roster: r }) => setRoster(r || []));

    socket.on('game:started', ({ totalWords: total }) => {
      setRemaining(total);
      setStatus('active');
    });

    socket.on('game:word_called', ({ word, definition, remaining: rem }) => {
      setCurrentWord({ word, definition });
      setCalledWords(prev => [...prev, { word, definition }]);
      setRemaining(rem);
      setCalling(false);
    });

    socket.on('game:bingo', ({ winners: w }) => {
      setWinners(w || []);
      setStatus('paused');
    });

    socket.on('game:resumed', () => setStatus('active'));

    socket.on('game:ended', () => setStatus('ended'));

    socket.on('game:round_reset', ({ totalWords: total }) => {
      setCalledWords([]);
      setCurrentWord(null);
      setRemaining(total ?? totalWordsRef.current);
      setWinners([]);
      setStatus('waiting'); // matches the server: a new round starts in the lobby
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('game:player_joined');
      socket.off('game:player_left');
      socket.off('game:player_disconnected');
      socket.off('game:player_reconnected');
      socket.off('game:player_marked');
      socket.off('game:started');
      socket.off('game:word_called');
      socket.off('game:bingo');
      socket.off('game:resumed');
      socket.off('game:ended');
      socket.off('game:round_reset');
    };
  }, [navigate, handleConnect, handleDisconnect]);

  function handleStart() {
    socket.emit('game:start', {}, (res) => {
      if (res?.ok) {
        setRemaining(res.totalWords ?? totalWords);
        setStatus('active');
        setCalling(true);
        socket.emit('game:next_clue', {}, (clueRes) => {
          if (clueRes?.error) setCalling(false);
        });
      }
    });
  }

  function handleKick(socketId, name) {
    if (!window.confirm(`Remove "${name}" from the lobby?`)) return;
    socket.emit('game:kick_player', { socketId });
  }

  function handleNextClue() {
    if (calling || remaining === 0 || status === 'paused') return;
    setCalling(true);
    socket.emit('game:next_clue', {}, (res) => {
      if (res?.error) {
        setCalling(false);
      }
      // UI updates via the game:word_called broadcast
    });
  }

  function handleNewRound() {
    socket.emit('game:reset', {}, (res) => {
      if (!res?.error) {
        setCalledWords([]);
        setCurrentWord(null);
        setWinners([]);
        setStatus('waiting');
      }
    });
  }

  function handleContinue() {
    socket.emit('game:resume', {}, (res) => {
      if (res?.ok) setStatus('active');
    });
  }

  function handleFinish() {
    socket.emit('game:finish', {}, (res) => {
      if (res?.ok) setStatus('ended');
    });
  }

  function handleEndGame() {
    setEnding(true);
    socket.emit('game:end', {}, () => {
      clearActiveRoomCode();
      socket.disconnect();
      navigate('/dashboard');
    });
  }

  const joinUrl = `${window.location.origin}/join?code=${code}`;

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.heading}>Classroom Bingo</h1>
          <p style={styles.sub}>You're hosting. Share the code or link with your students.</p>
        </div>
        <button onClick={handleEndGame} disabled={ending} style={styles.endBtn}>
          {ending ? 'Ending…' : 'End Game'}
        </button>
      </div>

      {/* ── Room info bar ── */}
      <div style={styles.roomCard}>
        <div style={styles.codeBlock}>
          <div style={styles.codeLabel}>Room Code</div>
          <div style={styles.code}>{code}</div>
        </div>
        <div style={styles.linkBlock}>
          <div style={styles.codeLabel}>Join Link</div>
          <div style={styles.link}>{joinUrl}</div>
        </div>
        <div style={styles.statsBlock}>
          <div style={styles.stat}>
            <span style={styles.statNum}>{roster.length}</span>
            <span style={styles.statLabel}>Players</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statNum}>{calledWords.length}</span>
            <span style={styles.statLabel}>Called</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statNum}>{remaining}</span>
            <span style={styles.statLabel}>Remaining</span>
          </div>
        </div>
      </div>

      {/* ── Winner banner ── */}
      {winners.length > 0 && (
        <div style={styles.winnerBanner}>BINGO! {winners.join(', ')} won!</div>
      )}

      {/* ── Bingo pause — teacher decides whether to keep playing ── */}
      {status === 'paused' && (
        <div style={styles.endedCard}>
          <p style={styles.endedText}>
            Game paused. Students can still claim a bingo on the current word.
          </p>
          <div style={styles.endedActions}>
            <button onClick={handleContinue} style={styles.newRoundBtn}>Continue Playing</button>
            <button onClick={handleFinish} style={styles.dashBtn}>Finish Game</button>
          </div>
        </div>
      )}

      {/* ── Waiting state — live roster ── */}
      {status === 'waiting' && (
        <div style={styles.lobbyLayout}>
          <div style={styles.rosterPanel}>
            <h2 style={styles.rosterHeading}>
              Students joined{roster.length > 0 ? ` (${roster.length})` : ''}
            </h2>
            {roster.length === 0 ? (
              <p style={styles.rosterEmpty}>
                No students yet — share the code or link above and watch names appear here.
              </p>
            ) : (
              <ul style={styles.rosterList}>
                {roster.map((p) => (
                  <li key={p.socketId} style={styles.rosterItem}>
                    <span style={styles.rosterName}>{p.name}</span>
                    <button
                      onClick={() => handleKick(p.socketId, p.name)}
                      style={styles.kickBtn}
                      title="Remove from lobby"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={styles.startPanel}>
            <button
              onClick={handleStart}
              disabled={roster.length === 0}
              style={{ ...styles.startBtn, ...(roster.length === 0 ? styles.startBtnDisabled : {}) }}
            >
              Start Game
            </button>
            <p style={styles.startHint}>
              {roster.length === 0
                ? 'Waiting for at least one student to join…'
                : `${roster.length} student${roster.length > 1 ? 's' : ''} ready`}
            </p>
          </div>
        </div>
      )}

      {/* ── Active state ── */}
      {(status === 'active' || status === 'paused') && (
        <div style={styles.activeLayout}>

          {/* Left: current clue + next button */}
          <div style={styles.cluePanel}>
            {currentWord ? (
              <div style={styles.currentClue}>
                {displayMode === 'definition' ? (
                  <div style={styles.currentWord}>{currentWord.word}</div>
                ) : (
                  <div style={styles.currentWord}>{currentWord.definition}</div>
                )}
              </div>
            ) : (
              <div style={styles.clueEmpty}>
                Press <strong>Next Clue</strong> to draw the first word.
              </div>
            )}

            <button
              onClick={handleNextClue}
              disabled={calling || remaining === 0 || status === 'paused'}
              style={{
                ...styles.nextBtn,
                ...(calling || remaining === 0 || status === 'paused' ? styles.nextBtnDisabled : {}),
              }}
            >
              {calling ? 'Drawing…' : remaining === 0 ? 'All words called!' : 'Next Clue →'}
            </button>

            {remaining === 0 && calledWords.length > 0 && (
              <div style={styles.exhaustedNote}>
                <p style={styles.exhaustedText}>All {calledWords.length} words have been called.</p>
                <button onClick={handleNewRound} style={styles.newRoundBtn}>New Round ↺</button>
              </div>
            )}
          </div>

          {/* Right column: history + student panel */}
          <div style={styles.rightColumn}>

            {/* History */}
            <div style={styles.historyPanel}>
              <h2 style={styles.panelHeading}>Called ({calledWords.length})</h2>
              {calledWords.length === 0 ? (
                <p style={styles.emptyText}>Nothing called yet.</p>
              ) : (
                <ul style={styles.calledList}>
                  {[...calledWords].reverse().map((w, i) => (
                    <li key={i} style={{
                      ...styles.calledItem,
                      ...(i === 0 ? styles.calledItemLatest : {}),
                    }}>
                      <span style={styles.calledWord}>{w.word}</span>
                      <span style={styles.calledDef}>{w.definition}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Student status panel */}
            <div style={styles.studentPanel}>
              <h2 style={styles.panelHeading}>Students ({roster.length})</h2>
              {roster.length === 0 ? (
                <p style={styles.emptyText}>No students.</p>
              ) : (
                <ul style={styles.studentList}>
                  {roster.map((p, i) => {
                    const winIdx = winners.indexOf(p.name);
                    const isWinner = winIdx !== -1;
                    const isDisconnected = !p.connected;
                    return (
                      <li key={p.socketId} style={{
                        ...styles.studentItem,
                        ...(isWinner ? styles.studentItemWinner : {}),
                        ...(isDisconnected ? styles.studentItemDisconnected : {}),
                      }}>
                        {isWinner && <span style={styles.trophy}>🏆</span>}
                        <span style={styles.studentName}>{p.name}</span>
                        {isWinner && (
                          <span style={styles.winnerBadge}>
                            {winIdx === 0 ? '1st' : winIdx === 1 ? '2nd' : winIdx === 2 ? '3rd' : `${winIdx + 1}th`}
                          </span>
                        )}
                        {isDisconnected
                          ? <span style={styles.disconnectedBadge}>away</span>
                          : <span style={styles.markedBadge}>{p.markedCount ?? 0}</span>
                        }
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── Game over ── */}
      {status === 'ended' && !ending && (
        <div style={styles.endedCard}>
          <p style={styles.endedText}>
            {winners.length > 0 ? `Game over — ${winners.join(', ')} won!` : 'Game ended.'}
          </p>
          <div style={styles.endedActions}>
            <button onClick={handleNewRound} style={styles.newRoundBtn}>New Round ↺</button>
            <button onClick={() => { clearActiveRoomCode(); socket.disconnect(); navigate('/dashboard'); }}
              style={styles.dashBtn}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

      {rejoinError && (
        <div style={styles.rejoinErrorBanner}>
          {rejoinError} —{' '}
          <button onClick={() => navigate('/dashboard')} style={styles.rejoinErrorBtn}>
            Back to Dashboard
          </button>
        </div>
      )}

      {!connected && !rejoinError && (
        <div style={styles.disconnectBanner}>
          {rejoining ? 'Reconnecting…' : 'Connection lost — reconnecting…'}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', backgroundColor: 'var(--color-background)',
    padding: '32px 24px', fontFamily: 'system-ui, sans-serif', maxWidth: '1000px', margin: '0 auto',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '24px', flexWrap: 'wrap', gap: '16px',
  },
  heading: { fontSize: '26px', fontWeight: '700', color: 'var(--color-text)', margin: '0 0 4px' },
  sub: { fontSize: '14px', color: '#6b6b7a', margin: 0 },
  endBtn: {
    padding: '10px 20px', backgroundColor: 'var(--color-destructive)', color: '#fff',
    border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer',
  },
  roomCard: {
    backgroundColor: 'var(--color-primary)', borderRadius: '14px', padding: '24px 28px',
    display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px', alignItems: 'center',
  },
  codeBlock: { flex: '0 0 auto' },
  codeLabel: { fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' },
  code: {
    display: 'inline-block', fontSize: '56px', fontWeight: '800', color: 'var(--color-primary)',
    letterSpacing: '0.14em', fontFamily: 'monospace', lineHeight: '1',
    backgroundColor: '#fff', padding: '10px 22px', borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
  },
  linkBlock: { flex: 1, minWidth: '200px' },
  link: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', wordBreak: 'break-all' },
  statsBlock: { display: 'flex', gap: '24px' },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statNum: { fontSize: '28px', fontWeight: '700', color: '#fff' },
  statLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' },
  winnerBanner: {
    backgroundColor: 'var(--color-accent)', color: '#fff', borderRadius: '10px',
    padding: '16px 24px', fontWeight: '700', fontSize: '20px',
    textAlign: 'center', marginBottom: '24px',
  },
  lobbyLayout: { display: 'grid', gridTemplateColumns: '1fr 260px', gap: '20px', alignItems: 'start' },
  rosterPanel: {
    backgroundColor: 'var(--color-background)', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e0d5', minHeight: '200px',
  },
  rosterHeading: { fontSize: '16px', fontWeight: '600', color: 'var(--color-text)', margin: '0 0 16px' },
  rosterEmpty: { color: '#9ca3af', fontSize: '14px', fontStyle: 'italic', lineHeight: '1.6' },
  rosterList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
  rosterItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', backgroundColor: 'color-mix(in srgb, var(--color-surface) 20%, var(--color-background))', border: '1px solid #e5e0d5',
    borderRadius: '8px',
  },
  rosterName: { fontSize: '15px', fontWeight: '500', color: 'var(--color-text)' },
  kickBtn: {
    background: 'none', border: 'none', color: '#9ca3af', fontSize: '12px',
    cursor: 'pointer', fontWeight: '500', padding: '2px 6px',
  },
  startPanel: {
    backgroundColor: 'var(--color-background)', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e0d5', textAlign: 'center',
  },
  startBtn: {
    padding: '14px 32px', backgroundColor: 'var(--color-primary)', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: '700', cursor: 'pointer',
    width: '100%', marginBottom: '12px',
  },
  startBtnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  startHint: { fontSize: '13px', color: '#6b6b7a', margin: 0 },
  activeLayout: { display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', alignItems: 'start' },
  rightColumn: { display: 'flex', flexDirection: 'column', gap: '16px' },
  cluePanel: {
    backgroundColor: 'var(--color-background)', borderRadius: '14px', padding: '32px',
    border: '1px solid #e5e0d5', textAlign: 'center',
  },
  currentClue: { marginBottom: '32px' },
  currentWord: {
    fontSize: '52px', fontWeight: '800', color: 'var(--color-text)',
    lineHeight: '1.1', marginBottom: '12px', letterSpacing: '-0.5px',
  },
  currentDef: { fontSize: '20px', color: '#6b6b7a', lineHeight: '1.5', maxWidth: '500px', margin: '0 auto' },
  clueEmpty: {
    fontSize: '16px', color: '#9ca3af', marginBottom: '32px',
    padding: '40px 0', lineHeight: '1.6',
  },
  nextBtn: {
    padding: '16px 48px', backgroundColor: 'var(--color-primary)', color: '#fff',
    border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: '700',
    cursor: 'pointer', letterSpacing: '0.01em',
  },
  nextBtnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  exhaustedNote: { marginTop: '16px' },
  exhaustedText: { fontSize: '14px', color: '#6b6b7a', margin: '0 0 12px' },
  historyPanel: {
    backgroundColor: 'var(--color-background)', borderRadius: '12px', padding: '20px',
    border: '1px solid #e5e0d5', maxHeight: '40vh', overflowY: 'auto',
  },
  studentPanel: {
    backgroundColor: 'var(--color-background)', borderRadius: '12px', padding: '20px',
    border: '1px solid #e5e0d5', maxHeight: '35vh', overflowY: 'auto',
  },
  studentList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
  studentItem: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '6px 10px', borderRadius: '6px',
    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
  },
  studentItemWinner: {
    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
  },
  studentItemDisconnected: {
    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', opacity: 0.45,
  },
  studentName: { fontSize: '13px', fontWeight: '500', color: 'var(--color-text)', flex: 1 },
  trophy: { fontSize: '13px' },
  winnerBadge: {
    fontSize: '10px', fontWeight: '700', color: '#15803d',
    backgroundColor: '#dcfce7', padding: '1px 5px', borderRadius: '4px',
  },
  disconnectedBadge: {
    fontSize: '10px', color: '#9ca3af', fontStyle: 'italic',
  },
  markedBadge: {
    fontSize: '11px', fontWeight: '600', color: '#6b7280',
    backgroundColor: '#f3f4f6', padding: '1px 6px', borderRadius: '10px',
    minWidth: '20px', textAlign: 'center',
  },
  panelHeading: { fontSize: '14px', fontWeight: '600', color: 'var(--color-text)', margin: '0 0 14px' },
  emptyText: { color: '#6b6b7a', fontSize: '14px', fontStyle: 'italic' },
  calledList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' },
  calledItem: {
    display: 'flex', flexDirection: 'column', padding: '8px 10px',
    backgroundColor: 'color-mix(in srgb, var(--color-surface) 15%, var(--color-background))', border: '1px solid #e5e7eb', borderRadius: '6px',
  },
  calledItemLatest: {
    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
  },
  calledWord: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  calledDef: { fontSize: '11px', color: '#6b7280', marginTop: '1px' },
  endedCard: {
    backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '12px',
    padding: '32px', textAlign: 'center',
  },
  endedText: { fontSize: '18px', color: 'var(--color-text)', marginBottom: '20px' },
  endedActions: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  newRoundBtn: {
    padding: '12px 24px', backgroundColor: 'var(--color-accent)', color: 'var(--color-text)',
    border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '15px', cursor: 'pointer',
  },
  dashBtn: {
    padding: '12px 24px', backgroundColor: 'var(--color-primary)', color: '#fff',
    border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '15px', cursor: 'pointer',
  },
  disconnectBanner: {
    position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
    backgroundColor: '#92400e', color: '#fff', borderRadius: '8px',
    padding: '12px 24px', fontWeight: '600', fontSize: '14px', zIndex: 999,
  },
  rejoinErrorBanner: {
    position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
    backgroundColor: '#dc2626', color: '#fff', borderRadius: '8px',
    padding: '12px 24px', fontWeight: '600', fontSize: '14px', zIndex: 999,
    whiteSpace: 'nowrap',
  },
  rejoinErrorBtn: {
    background: 'none', border: '1px solid rgba(255,255,255,0.7)', color: '#fff',
    borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '13px',
    fontWeight: '600',
  },
};
